import { useState } from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { Link } from 'react-router-dom'
import { Bot, Plus, Download, Trash2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { formatDistanceToNow } from 'date-fns'
import {
  useAssistants,
  useBulkDeleteAssistants,
  useCleanupTestAssistants,
  Assistant,
  TestAssistantPreview,
} from '@/hooks/useAssistants'
import { DataTable } from '@/components/common/DataTable'
import { useToast } from '@/hooks/use-toast'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const columns: ColumnDef<Assistant, any>[] = [
  {
    accessorKey: 'name',
    header: 'Name',
    cell: ({ row }) => {
      const urlId = row.original.shared_id || row.original.id
      return (
        <Link
          to={`/app/assistants/${urlId}`}
          className="font-medium text-primary hover:underline"
        >
          {row.original.name}
        </Link>
      )
    },
  },
  {
    accessorKey: 'description',
    header: 'Description',
    cell: ({ row }) => (
      <span className="text-muted-foreground line-clamp-1 max-w-[250px]">
        {row.original.description || '—'}
      </span>
    ),
  },
  {
    id: 'knowledge_bases',
    header: 'Knowledge Bases',
    cell: ({ row }) => {
      const kbs = row.original.knowledge_bases
      if (!kbs || kbs.length === 0) return <span className="text-muted-foreground italic">None</span>
      return (
        <span className="text-sm">
          {kbs.length} KB{kbs.length !== 1 ? 's' : ''}
        </span>
      )
    },
  },
  {
    id: 'status',
    header: 'Status',
    cell: ({ row }) => {
      const isActive = row.original.knowledge_bases?.length >= 1
      return (
        <Badge
          variant={isActive ? 'default' : 'secondary'}
          className={isActive ? 'bg-green-100 text-green-800 hover:bg-green-100' : ''}
        >
          {isActive ? 'active' : 'inactive'}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'created_at',
    header: 'Created',
    cell: ({ row }) =>
      formatDistanceToNow(new Date(row.original.created_at), { addSuffix: true }),
  },
]

export function AssistantsListPage() {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [search, setSearch] = useState('')

  const { data, isLoading } = useAssistants({ page, pageSize, search })
  const bulkDelete = useBulkDeleteAssistants()
  const cleanupMutation = useCleanupTestAssistants()
  const { toast } = useToast()

  // Cleanup dialog state
  const [cleanupDialogOpen, setCleanupDialogOpen] = useState(false)
  const [cleanupPreview, setCleanupPreview] = useState<TestAssistantPreview[] | null>(null)
  const [cleanupLoading, setCleanupLoading] = useState(false)

  const handleCleanupClick = () => {
    setCleanupLoading(true)
    cleanupMutation.mutate('preview', {
      onSuccess: (res) => {
        setCleanupPreview(res.items || [])
        setCleanupDialogOpen(true)
        setCleanupLoading(false)
      },
      onError: (err: any) => {
        toast({
          title: 'Failed to scan test assistants',
          description: err?.response?.data?.error || err.message,
          variant: 'destructive',
        })
        setCleanupLoading(false)
      },
    })
  }

  const handleCleanupConfirm = () => {
    cleanupMutation.mutate('confirm', {
      onSuccess: (res) => {
        toast({
          title: 'Cleanup complete',
          description: `Deleted ${res.deleted_count} test assistant(s) (${res.ragflow_deleted_count} from RAGflow)`,
        })
        setCleanupDialogOpen(false)
        setCleanupPreview(null)
      },
      onError: (err: any) => {
        toast({
          title: 'Cleanup failed',
          description: err?.response?.data?.error || err.message,
          variant: 'destructive',
        })
      },
    })
  }

  const handleBulkDelete = (ids: string[]) => {
    bulkDelete.mutate(ids, {
      onSuccess: (res) => {
        toast({ title: `Deleted ${res.deleted_count} assistant(s)` })
      },
      onError: () => {
        toast({ title: 'Failed to delete', variant: 'destructive' })
      },
    })
  }

  const emptyState = (
    <div className="flex flex-col items-center justify-center py-16">
      <Bot className="h-16 w-16 text-muted-foreground mb-4" />
      <h3 className="text-lg font-semibold mb-2">No assistants yet</h3>
      <p className="text-sm text-muted-foreground mb-6">
        Create your first AI assistant to get started
      </p>
      <Button asChild>
        <Link to="/app/assistants/create">
          <Plus className="h-4 w-4 mr-2" />
          Create Assistant
        </Link>
      </Button>
    </div>
  )

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Assistants</h1>
          <p className="text-muted-foreground mt-2">Manage your AI assistants</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCleanupClick}
            disabled={cleanupLoading}
          >
            {cleanupLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4 mr-2" />
            )}
            Cleanup Test
          </Button>
          <Button variant="outline" asChild>
            <Link to="/app/assistants/import">
              <Download className="h-4 w-4 mr-2" />
              Import from RAGflow
            </Link>
          </Button>
          <Button asChild>
            <Link to="/app/assistants/create">
              <Plus className="h-4 w-4 mr-2" />
              Create Assistant
            </Link>
          </Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data?.items ?? []}
        total={data?.total ?? 0}
        page={page}
        pageSize={pageSize}
        search={search}
        isLoading={isLoading}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSearchChange={setSearch}
        onBulkDelete={handleBulkDelete}
        isBulkDeleting={bulkDelete.isPending}
        getRowId={(row) => row.id}
        emptyState={emptyState}
      />

      {/* Cleanup Test Assistants Dialog */}
      <Dialog open={cleanupDialogOpen} onOpenChange={setCleanupDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Cleanup Test Assistants</DialogTitle>
            <DialogDescription>
              Found {cleanupPreview?.length ?? 0} test assistant(s) with the "test_assistant_" prefix.
              They will be deleted from both RAGflow and Chatterpillar.
            </DialogDescription>
          </DialogHeader>

          {cleanupPreview && cleanupPreview.length > 0 ? (
            <div className="max-h-[300px] overflow-y-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cleanupPreview.map((asst) => (
                    <TableRow key={asst.id}>
                      <TableCell className="font-mono text-xs">{asst.name}</TableCell>
                      <TableCell>
                        <Badge variant={asst.source === 'ragflow_only' ? 'outline' : 'secondary'} className="text-[10px]">
                          {asst.source === 'ragflow_only' ? 'RAGflow' : 'Both'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {asst.created_at ? (() => {
                          try { return formatDistanceToNow(new Date(asst.created_at), { addSuffix: true }) }
                          catch { return asst.created_at }
                        })() : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No test assistants found.
            </p>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCleanupDialogOpen(false)}
              disabled={cleanupMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleCleanupConfirm}
              disabled={cleanupMutation.isPending || !cleanupPreview?.length}
            >
              {cleanupMutation.isPending ? 'Deleting...' : `Delete ${cleanupPreview?.length ?? 0} Assistant(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
